import { Injectable, ViewContainerRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { GoJsSearchDialogParams, GoJsSearchNode, GoJsSearchNodeComponent, highlightNodes } from '@y42/gojs';
import { ToastService } from '@y42/toast';
import { BehaviorSubject, filter, tap } from 'rxjs';
import {
  OrchestrationTablesPickerPopupComponent,
  OrchestrationTablesPickerPopupInput,
  OrchestrationTablesPickerPopupOutput,
} from '../../orchestration-tables-picker-popup/orchestration-tables-picker-popup.component';
import { OrchestrationDiagramUIModel } from '../models/orchestration-diagram.model';
import { OrchestrationDiagramService } from '../services/orchestration-diagram.service';

@UntilDestroy()
@Injectable()
export class OrchestrationDiagramToolbarService {
  constructor(
    private readonly orchestrationDiagramService: OrchestrationDiagramService,
    private readonly dialog: MatDialog,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly toastService: ToastService,
  ) {
    this.orchestrationDiagramService.searchNodes$
      .pipe(
        tap((searchNodes) => (this._searchNodes = searchNodes)),
        untilDestroyed(this),
      )
      .subscribe();

    this.orchestrationDiagramService.outputModel$
      .pipe(
        tap((outputModel) => (this._outputModel = outputModel)),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private readonly _importLoader$$ = new BehaviorSubject<boolean>(false);
  private readonly _exportLoader$$ = new BehaviorSubject(false);
  private _searchNodes: GoJsSearchNode[] | undefined = [];
  private _outputModel: OrchestrationDiagramUIModel | undefined;

  readonly importLoader$ = this._importLoader$$.pipe(untilDestroyed(this));
  readonly exportLoader$ = this._exportLoader$$.pipe(untilDestroyed(this));

  private get diagram() {
    return this.orchestrationDiagramService.diagram;
  }

  alignNodes() {
    this.diagram.layoutDiagram(true);
  }

  undo() {
    this.diagram.undoManager.undo();
    this.diagram.clearHighlighteds();
  }

  redo() {
    this.diagram.undoManager.redo();
    this.diagram.clearHighlighteds();
  }

  searchOrchestrationNodes() {
    const data = { nodes: this._searchNodes } as GoJsSearchDialogParams;

    this.dialog
      .open<GoJsSearchNodeComponent, GoJsSearchDialogParams>(GoJsSearchNodeComponent, {
        width: '500px',
        height: '600px',
        data,
        panelClass: ['round-corner-modal'],
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        tap((response: GoJsSearchDialogParams) => highlightNodes([response.selectedNodeId], this.diagram)),
        untilDestroyed(this),
      )
      .subscribe();
  }

  addOrchestrationNodes() {
    this.dialog
      .open<
        OrchestrationTablesPickerPopupComponent,
        OrchestrationTablesPickerPopupInput,
        OrchestrationTablesPickerPopupOutput
      >(OrchestrationTablesPickerPopupComponent, {
        width: '90%',
        height: '90vh',
        data: {
          alreadySelectedAssets: (this._outputModel?.nodeDataArray || []).map((n) => n.path),
          allowDependencies: true,
        },
        autoFocus: false,
        viewContainerRef: this.viewContainerRef,
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        tap(({ selectedAssets, withDependencies }) =>
          this.orchestrationDiagramService.addDiagramNodes(selectedAssets, withDependencies),
        ),
      )
      .subscribe();
  }

  importJson(event: Event): void {
    try {
      const inputElem = event.target as HTMLInputElement;
      const fileList = inputElem?.files;
      if (fileList!.length < 1) {
        return;
      }
      this._importLoader$$.next(true);
      const file = fileList![0];

      const reader = new FileReader();
      reader.readAsText(file);

      reader.onload = () => {
        this.processJson(reader.result as string);
        inputElem.value = '';
        this._importLoader$$.next(false);
      };

      reader.onerror = () => {
        console.log(reader.error);
        this._importLoader$$.next(false);
        this.toastService.error(reader.error?.message);
      };
    } catch (error) {
      console.log(error);
      this._importLoader$$.next(false);
    }
  }

  exportJson(downloadLink: HTMLAnchorElement) {
    try {
      this._exportLoader$$.next(true);
      const outputModel = this._outputModel;
      if (!outputModel) {
        return;
      }

      if (outputModel.nodeDataArray.length < 1) {
        this.toastService.error('No Data to download');
        this._exportLoader$$.next(false);
        return;
      }
      const dataStr = `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(outputModel))}`;
      const dlElem = downloadLink;
      dlElem.setAttribute('href', dataStr);
      dlElem.setAttribute('download', `y42_orchestration.json`);
      dlElem.click();
      this._exportLoader$$.next(false);
    } catch (error) {
      console.log(error);
      this._exportLoader$$.next(false);
    }
  }

  private processJson(jsonString: string): void {
    try {
      const model = JSON.parse(jsonString) as OrchestrationDiagramUIModel;
      if (this.validateOrchestrationDiagramModel(model) !== 'valid') {
        this.toastService.error(this.validateOrchestrationDiagramModel(model));
        return;
      }

      if (model.nodeDataArray.length < 1) {
        this.toastService.error('Not sufficient data to import!');
        return;
      }
      this.orchestrationDiagramService.addImportedModel(model);
    } catch (error) {
      console.log(error);
      this.toastService.error('Invalid JSON File!');
    }
  }

  private validateOrchestrationDiagramModel(model: OrchestrationDiagramUIModel): string {
    if (!('nodeDataArray' in model) || !Array.isArray(model['nodeDataArray'])) {
      return 'Missing or Invalid nodeDataArray Property';
    }

    if (!('linkDataArray' in model) || !Array.isArray(model['linkDataArray'])) {
      return 'Missing or Invalid linkDataArray Property';
    }

    const { nodeDataArray, linkDataArray } = model;

    for (const node of nodeDataArray) {
      if (!('key' in node) || typeof node['key'] !== 'string') {
        return 'Node: Missing or Invalid Key Property';
      }
    }

    for (const link of linkDataArray) {
      if (!('to' in link) || typeof link['to'] !== 'string') {
        return 'Link: Missing or Invalid to Property';
      }
      if (!('from' in link) || typeof link['from'] !== 'string') {
        return 'Link: Missing or Invalid from Property';
      }
    }

    return 'valid';
  }
}
